import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Button, Stack } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import { staffAction } from '../../data/dataProvider';
import { ActionDialog } from '../../components/ActionDialog';
import { useCanModerate } from '../../auth/useCanModerate';

/**
 * Решение по открытой жалобе: подтвердить (upheld, -5 рейтинга ответчику,
 * необратимо) или отклонить (dismissed). С диалогом подтверждения.
 */
export function ResolveReportButtons(): JSX.Element | null {
  const record = useRecordContext<{ id: string; status: string }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const canModerate = useCanModerate();
  const [dialog, setDialog] = useState<'upheld' | 'dismissed' | null>(null);

  if (!record || !canModerate || record.status !== 'open') return null;

  async function confirm(notes: string): Promise<void> {
    if (!record || !dialog) return;
    try {
      await staffAction('POST', `/staff/reports/${record.id}/resolve`, {
        decision: dialog,
        notes: notes || undefined,
      });
      notify(
        dialog === 'upheld' ? 'Жалоба подтверждена (-5 рейтинга ответчику)' : 'Жалоба отклонена',
        { type: 'success' },
      );
      setDialog(null);
      refresh();
    } catch (e) {
      notify(`Ошибка: ${(e as Error).message}`, { type: 'error' });
    }
  }

  return (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        color="error"
        startIcon={<GavelIcon />}
        onClick={(e) => {
          e.stopPropagation();
          setDialog('upheld');
        }}
      >
        Подтвердить
      </Button>
      <Button
        size="small"
        color="inherit"
        startIcon={<ThumbDownOffAltIcon />}
        onClick={(e) => {
          e.stopPropagation();
          setDialog('dismissed');
        }}
      >
        Отклонить
      </Button>
      <ActionDialog
        open={dialog === 'upheld'}
        title="Подтвердить жалобу?"
        description="Ответчик получит -5 к рейтингу. Решение по жалобе окончательное — отменить его нельзя (компенсация возможна только ручной корректировкой рейтинга администратором)."
        confirmLabel="Подтвердить жалобу"
        confirmColor="error"
        reason={{ label: 'Комментарий модератора', required: false }}
        onConfirm={confirm}
        onClose={() => setDialog(null)}
      />
      <ActionDialog
        open={dialog === 'dismissed'}
        title="Отклонить жалобу?"
        description="Жалоба будет закрыта без последствий для ответчика. Решение окончательное."
        confirmLabel="Отклонить жалобу"
        confirmColor="primary"
        reason={{ label: 'Комментарий модератора', required: false }}
        onConfirm={confirm}
        onClose={() => setDialog(null)}
      />
    </Stack>
  );
}
